/**
 * Cowork Home Page — discovery-first landing.
 * Layout: greeting → input → pinned projects → curated use-case sections.
 * Use cases are catalogued by marketing function (Paid, Social, Strategy,
 * SEO & Content, Influencer, Email & CRM, Analytics) — each card opens a
 * chat with the right agent pre-loaded with the prompt.
 */

import { useEffect, useState, useRef, type ComponentType } from 'react';
import {
  Send, Paperclip, Mic, ArrowRight,
  // Paid
  Megaphone, Sparkles, Wand2, DollarSign, Pause, TrendingUp,
  // Social
  Share2, CalendarDays, MessageCircle, Hash, Repeat, Activity,
  // Strategy
  Compass, Layers, Crosshair, Target, Briefcase, UserCog,
  // SEO + Content
  Search, FileText, Link2, BookOpen, Globe, ListTree,
  // Influencer
  Users, MailPlus, ShieldCheck, BarChart2, FileSignature, Handshake,
  // Email/CRM
  Mail, Workflow, MailCheck, Filter, Send as SendIcon, FlaskConical,
  // Analytics
  BarChart3, FileBarChart, LineChart, GitFork, PieChart, Gauge,
  // Misc
  Loader2,
} from 'lucide-react';
import { getUserFromStorage } from '@/utils/auth';
import { coworkApi } from '../api/coworkApi';
import type { Project } from '../types/cowork';

// ── Use-case catalogue ──────────────────────────────────────────────────────
//
// Each card has: title (≤6 words), description (≤14 words), icon, agent, and
// a pre-filled message that lands the agent in the right context.

interface UseCase {
  title: string;
  description: string;
  icon: ComponentType<{ className?: string }>;
  agent: string;
  message: string;
}

interface Section {
  id: string;
  title: string;
  description: string;
  /** Tailwind-friendly hex strings for accent + soft tint backgrounds. */
  accent: string;
  tint: string;
  items: UseCase[];
}

/**
 * Feature scope — 'core' surfaces only the sections that map to Beatly's
 * campaign process (influencer/campaign work, amplification, reporting);
 * 'full' restores the marketing-OS catalogue. Content is untouched either way.
 */
const FEATURE_SCOPE = (import.meta.env.VITE_FEATURE_SCOPE as string) || 'core';
const CORE_SECTION_IDS = new Set(['influencer', 'paid', 'analytics']);

const SECTIONS: Section[] = [
  {
    id: 'paid',
    title: 'Paid media',
    description: 'Plan, launch, and optimise ad spend across channels.',
    accent: '#0EA5E9', tint: '#F0F9FF',
    items: [
      { title: 'Audit my Google Ads account',          description: 'Spot waste, opportunity, and quick wins across campaigns.', icon: Megaphone,  agent: 'account-manager', message: 'Audit our Google Ads account and surface the top 5 issues + 3 quick wins.' },
      { title: 'Analyse Meta campaign performance',    description: 'Last 30 days — what worked, what to kill, what to scale.',   icon: TrendingUp, agent: 'account-manager', message: 'Analyse our Meta Ads performance over the last 30 days and recommend what to kill and what to scale.' },
      { title: 'Generate ad copy variations',          description: '10 RSA / paid-social headlines tuned to our brand voice.',   icon: Wand2,      agent: 'aria-content',    message: 'Generate 10 ad copy variations (headlines + descriptions) for our top-performing campaign in our brand voice.' },
      { title: 'Build audiences + lookalikes',         description: 'Cluster customers + propose seed lookalike audiences.',      icon: Sparkles,   agent: 'account-manager', message: 'Build 3 audience clusters from our customer data and propose seed lookalike audiences for Meta and Google.' },
      { title: 'Pause underperforming creative',       description: 'Find creative below CPA target and queue them to pause.',    icon: Pause,      agent: 'account-manager', message: 'Find ad creatives that are 30% above target CPA and queue them to pause with my approval.' },
      { title: 'Forecast budget pacing',               description: 'Are we on track for the quarter? What needs to flex?',      icon: DollarSign, agent: 'kai-analytics',   message: 'Forecast our paid media spend for the rest of the quarter and flag any pacing issues.' },
    ],
  },
  {
    id: 'social',
    title: 'Social management',
    description: 'Plan content, ship posts, listen to audiences.',
    accent: '#EC4899', tint: '#FDF2F8',
    items: [
      { title: 'Draft this week’s social posts',       description: '5 posts across LinkedIn, X, and Instagram in our voice.',     icon: Share2,        agent: 'aria-content', message: 'Draft 5 social posts for this week — mix of LinkedIn, X, and Instagram — in our brand voice.' },
      { title: 'Plan a 30-day content calendar',       description: 'Mapped to our content pillars and posting cadence.',          icon: CalendarDays,  agent: 'aria-content', message: 'Plan a 30-day content calendar mapped to our content pillars. Include format, channel, and CTA.' },
      { title: 'Write a LinkedIn thought-leader post', description: 'On a topic we own. Tight, useful, no fluff.',                 icon: MessageCircle, agent: 'aria-content', message: 'Write a strong LinkedIn thought-leadership post on a topic we should be known for. Tight, useful, no fluff.' },
      { title: 'Repurpose a blog into a thread',       description: 'One long-form post into a 7-tweet thread.',                   icon: Repeat,        agent: 'aria-content', message: 'Pick one of our recent blog posts and repurpose it into a 7-tweet thread for X/Twitter.' },
      { title: 'Find trending hashtags',               description: 'Niche-relevant tags worth using this week.',                  icon: Hash,          agent: 'aria-content', message: 'Find 10 trending hashtags relevant to our niche right now and tell me which to use this week.' },
      { title: 'Summarise last week’s engagement',     description: 'What got attention, what flopped, what to repeat.',           icon: Activity,      agent: 'kai-analytics', message: 'Summarise our social engagement from last week — what got attention, what flopped, what to repeat.' },
    ],
  },
  {
    id: 'strategy',
    title: 'Strategy & brand',
    description: 'Position, plan, and decide where the brand goes next.',
    accent: '#7C3AED', tint: '#F5F3FF',
    items: [
      { title: 'Audit our brand identity',             description: 'Voice, visual, positioning — surfaces weaknesses.',           icon: Compass,    agent: 'account-manager', message: 'Audit our brand identity (voice, visual, positioning) and tell me where the gaps are.' },
      { title: 'Map competitive positioning',          description: '2x2 plus a written narrative of where to play.',              icon: Layers,     agent: 'account-manager', message: 'Map our competitive positioning vs the top 5 competitors. Include a 2x2 and a where-to-play narrative.' },
      { title: 'Run a SWOT for the next move',         description: 'Strengths, weaknesses, opportunities, threats — written.',   icon: Crosshair,  agent: 'account-manager', message: 'Run a SWOT analysis for our next strategic move. Be specific.' },
      { title: 'Draft a quarterly OKR set',            description: 'One objective + 3 KRs aligned to our growth thesis.',         icon: Target,     agent: 'account-manager', message: 'Draft a quarterly OKR set: one objective and three measurable key results aligned to our current growth thesis.' },
      { title: 'Propose a project plan',               description: 'Pick a campaign / launch / refresh and scope it.',            icon: Briefcase,  agent: 'account-manager', message: 'Propose a project plan for our next big initiative — tasks, OKRs, owners, and a timeline.' },
      { title: 'Profile our ICP',                      description: 'Decision-maker persona, motivations, channels.',              icon: UserCog,    agent: 'account-manager', message: 'Profile our ideal customer (ICP). Decision-maker persona, motivations, channels, objections.' },
    ],
  },
  {
    id: 'seo-content',
    title: 'SEO & content',
    description: 'Win search, ship long-form, optimise pages.',
    accent: '#16A34A', tint: '#F0FDF4',
    items: [
      { title: 'Run an SEO audit',                     description: 'Technical + on-page + content gaps in one report.',           icon: Search,    agent: 'maya-seo',     message: 'Run an SEO audit on our site. Technical + on-page + content gaps. Prioritised by impact.' },
      { title: 'Find keyword opportunities',           description: 'Top 50 keywords we could rank for in 90 days.',               icon: Globe,     agent: 'maya-seo',     message: 'Find 50 keyword opportunities we could realistically rank for in the next 90 days. Include intent + difficulty.' },
      { title: 'Brief a pillar article',               description: 'Outline + word count + competitive coverage analysis.',       icon: BookOpen,  agent: 'maya-seo',     message: 'Brief a long-form pillar article on a high-value topic. Outline, word count, competitive coverage, suggested H2s.' },
      { title: 'Optimise a page for ranking',          description: 'On-page suggestions: title, headers, internal links.',        icon: FileText,  agent: 'maya-seo',     message: 'Pick one of our existing pages and propose on-page optimisations: title, headers, internal links, schema.' },
      { title: 'Build an internal-linking map',        description: 'Hub + spoke structure that lifts pillar pages.',              icon: ListTree,  agent: 'maya-seo',     message: 'Build an internal-linking map for our site — hub + spoke — that strengthens our pillar pages.' },
      { title: 'Track SERP rankings',                  description: 'Weekly view of where we’re moving up or slipping.',           icon: Link2,     agent: 'maya-seo',     message: 'Set up a weekly SERP-tracking report for our top 30 keywords.' },
    ],
  },
  {
    id: 'influencer',
    title: 'Influencer & creator',
    description: 'Source talent, run activations, measure lift.',
    accent: '#F59E0B', tint: '#FFFBEB',
    items: [
      { title: 'Source 30 micro-creators',             description: 'Filter by niche, country, engagement, follower band.',        icon: Users,         agent: 'rex-influencer', message: 'Source 30 micro-creators for our brand — filter by niche, country, engagement rate, and follower band.' },
      { title: 'Draft outreach email',                 description: 'Friendly, specific, no copy-paste vibes.',                    icon: MailPlus,      agent: 'rex-influencer', message: 'Draft a creator outreach email template — friendly, specific to each creator, no generic copy-paste energy.' },
      { title: 'Check disclosure compliance',          description: 'Review draft posts for #ad / sponsored compliance.',          icon: ShieldCheck,   agent: 'rex-influencer', message: 'Review our recent influencer posts for disclosure compliance (FTC / EU / UK ASA rules) and flag any issues.' },
      { title: 'Measure brand-lift',                   description: 'Pre/post survey or social-listening signal.',                 icon: BarChart2,     agent: 'kai-analytics',  message: 'Help me measure brand-lift from our last creator campaign — pre/post survey or social-listening signals.' },
      { title: 'Build a creator brief',                description: 'Goals, dos / don’ts, deliverables, timeline.',                icon: FileSignature, agent: 'rex-influencer', message: 'Build a creator brief for an upcoming activation — goals, dos and don’ts, deliverables, timeline.' },
      { title: 'Negotiate creator fees',               description: 'Benchmark + a counter-offer template.',                       icon: Handshake,     agent: 'rex-influencer', message: 'Help me benchmark creator fees for our shortlist and draft a counter-offer template.' },
    ],
  },
  {
    id: 'email-crm',
    title: 'Email & CRM',
    description: 'Drive lifecycle: nurture, retention, reactivation.',
    accent: '#0891B2', tint: '#ECFEFF',
    items: [
      { title: 'Write a welcome sequence',             description: '3 emails that activate new signups in week 1.',               icon: Mail,         agent: 'aria-content',   message: 'Write a 3-email welcome sequence that activates new signups in their first week. Brand voice, clear CTAs.' },
      { title: 'Build a re-engagement flow',           description: 'Win back users who’ve gone quiet for 30+ days.',              icon: Workflow,     agent: 'aria-content',   message: 'Build a re-engagement email flow for users who’ve gone quiet for 30+ days. Subject lines + bodies.' },
      { title: 'Audit email deliverability',           description: 'SPF / DKIM / DMARC + recent inbox-placement signal.',         icon: MailCheck,    agent: 'kai-analytics',  message: 'Audit our email deliverability — SPF, DKIM, DMARC + recent inbox-placement signals.' },
      { title: 'Segment list by behaviour',            description: 'High-value, dormant, free-to-paid candidates.',               icon: Filter,       agent: 'kai-analytics',  message: 'Segment our email list by behaviour — high-value, dormant, free-to-paid candidates. Define each.' },
      { title: 'Draft nurture for trial users',        description: '5 emails that move trials toward conversion.',                icon: SendIcon,     agent: 'aria-content',   message: 'Draft a 5-email nurture sequence that moves trial users toward conversion. Include a hard ask.' },
      { title: 'A/B test subject lines',               description: 'Plan a 2-variant test with statistical horizon.',             icon: FlaskConical, agent: 'kai-analytics',  message: 'Plan a 2-variant subject-line A/B test — hypotheses, sample size, statistical horizon, success metric.' },
    ],
  },
  {
    id: 'analytics',
    title: 'Analytics & reporting',
    description: 'Read the numbers, find the why, decide next.',
    accent: '#8B5CF6', tint: '#FAF5FF',
    items: [
      { title: 'Build an executive dashboard',         description: 'Top KPIs across paid, social, SEO, lifecycle.',               icon: BarChart3,     agent: 'kai-analytics', message: 'Build an executive dashboard with our top KPIs across paid, social, SEO, and lifecycle.' },
      { title: 'Generate a weekly report',             description: 'What moved this week, why it moved, what to do.',             icon: FileBarChart,  agent: 'kai-analytics', message: 'Generate this week’s performance report — what moved, why it moved, and what to do about it.' },
      { title: 'Run cohort retention analysis',        description: 'Compare cohorts and surface the leaky spots.',                icon: LineChart,     agent: 'kai-analytics', message: 'Run a cohort retention analysis on our customer base — compare cohorts and surface the leaky spots.' },
      { title: 'Find drop-off in the funnel',          description: 'Where users disappear and what to fix first.',                icon: GitFork,       agent: 'kai-analytics', message: 'Find the biggest drop-off in our acquisition funnel and propose what to fix first.' },
      { title: 'Compare attribution models',           description: 'Last-click vs data-driven — what changes our story.',         icon: PieChart,      agent: 'kai-analytics', message: 'Compare last-click vs data-driven attribution for our paid channels and tell me how the story changes.' },
      { title: 'Forecast next-month KPIs',             description: 'Trend lines + a confidence band.',                            icon: Gauge,         agent: 'kai-analytics', message: 'Forecast our top KPIs for next month with confidence bands. Flag the ones at risk.' },
    ],
  },
];

// ── Props ────────────────────────────────────────────────────────

interface CoworkHomePageProps {
  onNavigateToChat: (agentId: string, message?: string) => void;
  onNavigateToView: (view: string) => void;
  brandId?: string;
}

// ── Helpers ──────────────────────────────────────────────────────

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

// When no profile name exists, `user.name` falls back to the email local part
// ("guilherme.gomes+omni1"). Turn that into a presentable first name.
function presentableFirstName(raw: string | undefined): string {
  if (!raw) return 'there';
  const first = raw.split(' ')[0].split(/[._+\-]/)[0];
  if (!first || /\d/.test(first)) return 'there';
  return first.charAt(0).toUpperCase() + first.slice(1);
}

// ── Component ────────────────────────────────────────────────────

export default function CoworkHomePage({ onNavigateToChat, onNavigateToView, brandId }: CoworkHomePageProps) {
  const user = getUserFromStorage();
  const firstName = user?.first_name || presentableFirstName(user?.name);

  const [input, setInput] = useState('');
  const [pinnedProjects, setPinnedProjects] = useState<Project[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Pinned projects strip
  useEffect(() => {
    coworkApi.projects.list({ brandId, pinnedOnly: true })
      .then((d) => setPinnedProjects(d.projects))
      .catch(() => setPinnedProjects([]));
  }, [brandId]);

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 120) + 'px';
    }
  }, [input]);

  const handleSend = () => {
    if (!input.trim()) return;
    // Navigate to ACMO chat with the message
    onNavigateToChat('account-manager', input.trim());
    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-[720px] mx-auto px-6 pt-16 pb-12">

        {/* ── Greeting ── */}
        <div className="text-center mb-8">
          <h1 className="text-[28px] text-[#101828] mb-1" style={{ fontWeight: 700 }}>
            {getGreeting()}, {firstName}
          </h1>
          <p className="text-[15px] text-[#98a2b3]">
            What would you like to work on today?
          </p>
        </div>

        {/* ── Action Bar ── */}
        <div className="mb-8">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 hover:shadow-md transition-shadow">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask Lykke anything — strategy, campaigns, reports, content..."
              rows={1}
              className="w-full resize-none bg-transparent text-[14px] text-[#101828] placeholder:text-[#c4c9d4] outline-none leading-relaxed"
              style={{ minHeight: 24 }}
            />
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
              <div className="flex items-center gap-2">
                <button className="w-8 h-8 rounded-lg flex items-center justify-center text-[#98a2b3] hover:bg-gray-100 hover:text-[#667085] transition-all">
                  <Paperclip className="w-4 h-4" />
                </button>
                <button className="w-8 h-8 rounded-lg flex items-center justify-center text-[#98a2b3] hover:bg-gray-100 hover:text-[#667085] transition-all">
                  <Mic className="w-4 h-4" />
                </button>
              </div>
              <button
                onClick={handleSend}
                disabled={!input.trim()}
                className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#be43ff] text-white disabled:opacity-30 hover:bg-[#a835e0] transition-all"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* ── Pinned projects strip ── */}
        {pinnedProjects.length > 0 && (
          <div className="mb-6">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-[#94a3b8] mb-2">Pinned projects</div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {pinnedProjects.map((p) => (
                <button
                  key={p.id}
                  onClick={() => onNavigateToView(`project-detail:${p.id}`)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-gray-100 hover:border-[#be43ff]/30 hover:bg-[#faf5ff] transition-all shrink-0"
                >
                  <span className="text-[16px]">{p.emoji || '📁'}</span>
                  <span className="text-[12px] font-medium text-[#101828] truncate max-w-[180px]">{p.title}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Use case sections ── */}
        <div className="flex flex-col gap-10">
          {SECTIONS.filter((s) => FEATURE_SCOPE === 'full' || CORE_SECTION_IDS.has(s.id)).map((section) => (
            <UseCaseSection
              key={section.id}
              section={section}
              onPick={(useCase) => onNavigateToChat(useCase.agent, useCase.message)}
            />
          ))}
        </div>

        <div className="mt-12 flex flex-col items-center gap-2 text-center">
          <p className="text-[12px] text-[#94a3b8]" style={{ fontWeight: 500 }}>
            Don’t see what you need?
          </p>
          <button
            onClick={() => onNavigateToChat('account-manager', '')}
            className="flex items-center gap-1.5 text-[13px] text-[#be43ff] hover:underline"
            style={{ fontWeight: 600 }}
          >
            Ask your CMO directly
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>

      </div>
    </div>
  );
}

// ── Use-case section ───────────────────────────────────────────────────────

function UseCaseSection({
  section, onPick,
}: { section: Section; onPick: (u: UseCase) => void }) {
  return (
    <section>
      <div className="flex items-end justify-between mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: section.accent }}
            />
            <h2
              className="text-[11px] text-[#94a3b8]"
              style={{
                fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
              }}
            >
              {section.title}
            </h2>
          </div>
          <p className="text-[13px] text-[#475569]" style={{ fontWeight: 500 }}>
            {section.description}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {section.items.map((u, i) => (
          <UseCaseCard key={i} useCase={u} accent={section.accent} tint={section.tint} onPick={onPick} />
        ))}
      </div>
    </section>
  );
}

function UseCaseCard({
  useCase, accent, tint, onPick,
}: {
  useCase: UseCase; accent: string; tint: string; onPick: (u: UseCase) => void;
}) {
  const Icon = useCase.icon;
  return (
    <button
      onClick={() => onPick(useCase)}
      className="group relative text-left rounded-xl bg-white border border-gray-100 hover:shadow-md hover:-translate-y-px transition-all p-4 overflow-hidden"
    >
      {/* Subtle accent stripe on hover */}
      <span
        className="absolute inset-x-0 top-0 h-[2px] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300"
        style={{ background: accent }}
      />
      <div className="flex items-start gap-3">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-colors"
          style={{ background: tint, color: accent }}
        >
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <h3
            className="text-[13px] text-[#0f172a] leading-snug"
            style={{ fontWeight: 600 }}
          >
            {useCase.title}
          </h3>
          <p className="text-[11.5px] text-[#64748b] mt-1 leading-snug">
            {useCase.description}
          </p>
        </div>
        <ArrowRight
          className="w-3.5 h-3.5 text-[#cbd5e1] group-hover:text-[var(--accent)] shrink-0 mt-0.5 transition-colors"
          style={{ ['--accent' as any]: accent }}
        />
      </div>
    </button>
  );
}
